import {
  BranchStepTypes,
  LoopStepTypes,
  LoopVariable,
  type Step,
} from '../types';

import { AUTOMATION_VARIABLE_DATA_TYPES, MAX_LOOP_DEPTH } from '../constants';

import { AUTOMATION_STEPS, isVariableStep } from 'pages/AutomationEngine/steps';
import { current } from 'immer';
import { type TFunction } from 'react-i18next';

export const safeCurrent = (o: any) => {
  try {
    return current(o);
  } catch (error) {
    return o;
  }
};

export const MAX_VARIABLE_LENGTH = 100;

const orderSteps = (a: Step, b: Step) => (a.order || 0) - (b.order || 0);

export const splitAndPrepStepsAndVariables = (apiStepsAndVariables: Step[]) => {
  const stepsAndVariables = apiStepsAndVariables.sort(orderSteps);

  let steps = stepsAndVariables
    .filter((f) => !isVariableStep(f))
    .map((s, order) => ({ ...s, order }));

  const variables = stepsAndVariables.filter(isVariableStep);

  if (steps.length && variables.length) {
    const variableIds = variables.map((v) => v.id);

    steps = steps.map((step) => {
      return {
        ...step,
        parentStepId: variableIds.includes(step.parentStepId!)
          ? null
          : step.parentStepId,
      };
    });
  }

  return [steps, variables];
};

export const insertVariable = (variables: Step[], step: Step) => {
  const { regularVariables, addNew, itemVariables } = variables.reduce(
    (prev, v) => {
      if (v.type === 'initialize_variable_add') {
        prev.addNew.push(step, v);
      } else if (!v?.details?.variable?.isArrayItemVariable) {
        prev.regularVariables.push(v);
      } else {
        prev.itemVariables.push(v);
      }
      return prev;
    },
    { regularVariables: [], addNew: [], itemVariables: [] } as {
      regularVariables: Array<Step>;
      addNew: Array<Step>;
      itemVariables: Array<Step>;
    }
  );

  let parentStepId: string | null = null;

  const orderVars = [...regularVariables, ...addNew, ...itemVariables].map(
    (v, order) => {
      const ret = { ...v, order, parentStepId };
      parentStepId = v.id;
      return ret;
    }
  );

  return orderVars;
};

export const getUniqueName = (value: string, names: string[]) => {
  let nextValue = (value as string).trim();

  const uniqueNames = new Set(names);

  const copyRegEx = new RegExp(`^.+_copy_\\d+$`);
  const copyNumberRegEx = new RegExp(`_copy_\\d+$`);

  if (uniqueNames.has(nextValue)) {
    let i = 1;
    do {
      nextValue = nextValue.match(copyRegEx)
        ? nextValue.replace(copyNumberRegEx, `_copy_${i}`)
        : `${nextValue}_copy_${i}`;
      i++;
    } while (uniqueNames.has(nextValue));
  }

  return nextValue;
};

// A loop cannot iterate an array that one of the loops it sits inside is
// already iterating - the two would share the same item at different depths.
export const isArrayVariableInUseByLoop = (
  arrayVariableName: string | undefined | null,
  loopVariables: Array<LoopVariable> = []
) =>
  Boolean(arrayVariableName) &&
  loopVariables.some(({ variableName }) => variableName === arrayVariableName);

// What has to happen to a loop's item reference variable when its array
// variable is swapped.
export const ITEM_VARIABLE_CHANGES = {
  // nothing to do
  None: 'none',
  // first array variable chosen - initialize the item reference variable
  Create: 'create',
  // AC: the new array holds the same item shape - the same Object, or another
  // string/uuid array - so the item reference variable keeps its name and every
  // step inside the loop goes on referencing it. Only the array it draws from
  // moves, and the item variable follows that array's type.
  Repoint: 'repoint',
  // a different item shape - a different Object, or crossing between an Object
  // Record array and a string/uuid one - means the old variable is replaced and
  // steps still referencing it are meant to error on step save
  Replace: 'replace',
} as const;

export type ItemVariableChange = ValueOf<typeof ITEM_VARIABLE_CHANGES>;

// The shape of what an array variable hands the loop on each pass. Two arrays
// of the same shape mean the item reference variable can survive a swap between
// them; a different shape means it has to be replaced.
//
// An Object Record array is shaped by its Object. String and uuid arrays all
// share one shape, so swapping between any of them keeps the item reference
// variable (AC 1.a.i), while crossing between a scalar array and an Object
// Record array replaces it (AC 1.a.ii, 1.a.iii, 1.b).
const itemShapeOfArrayVariable = (arrayVariable: any) => {
  // The selection is a picker option in session and the API variable it was
  // saved as after a reload; both carry the type under `meta`. Only an Object
  // Record array has an Object, so it identifies the shape on its own where an
  // older selection reaches us without a dataType.
  const { dataType, dataSubtype } = arrayVariable?.meta ?? {};

  return dataType === AUTOMATION_VARIABLE_DATA_TYPES.entity ||
    dataSubtype?.value
    ? `entity:${dataSubtype?.value}`
    : 'scalar';
};

export const getItemVariableChange = (
  forEachItem: any,
  prevForEachItem: any
): ItemVariableChange => {
  const arrayVariable = forEachItem?.arrayVariable;
  const prevArrayVariable = prevForEachItem?.arrayVariable;

  if (!arrayVariable || !forEachItem?.itemVariable) {
    return ITEM_VARIABLE_CHANGES.None;
  }

  if (!prevArrayVariable) {
    return ITEM_VARIABLE_CHANGES.Create;
  }

  if (prevArrayVariable.value === arrayVariable.value) {
    return ITEM_VARIABLE_CHANGES.None;
  }

  return itemShapeOfArrayVariable(prevArrayVariable) ===
    itemShapeOfArrayVariable(arrayVariable)
    ? ITEM_VARIABLE_CHANGES.Repoint
    : ITEM_VARIABLE_CHANGES.Replace;
};

// Repointing a loop at another array of the same item shape keeps the item
// reference variable - its name is what steps inside the loop reference - but
// the variable still describes what the loop hands each pass, so its type has
// to follow the array it now draws from. A string array swapped for a uuid one
// is the case that moves; between two arrays of the same Object nothing does.
export const applyItemVariableSource = (
  itemVariable: any,
  sourceArrayVariable: string,
  sourceVariable?: any
) => {
  if (!itemVariable?.details?.variable) {
    return itemVariable;
  }

  itemVariable.details.variable.sourceArrayVariable = sourceArrayVariable;

  const source = sourceVariable?.details?.variable;

  if (source) {
    const { dataType, dataSubtype, outputFormat } = source;

    Object.assign(itemVariable.details.variable, {
      dataType,
      dataSubtype,
      outputFormat,
    });
  }

  return itemVariable;
};

// Every array variable a loop iterates, its nested loops included - all of them
// would be running at once wherever the loop is dropped.
export const getLoopArrayVariableNames = (
  steps: Array<Step>,
  loopStep: Step
): Array<string> => {
  const bodyIds = new Set(getStepIdsInLoop(steps, loopStep));

  return [
    loopStep,
    ...steps.filter((step) => bodyIds.has(step.id) && isLoopStep(step)),
  ]
    .map((step: any) => step?.details?.forEachItem?.arrayVariable?.value)
    .filter(Boolean);
};

// The array variables that would end up being iterated twice over if this loop
// were dropped inside the given loops - by the loop itself or by one nested in
// it. Empty when the drop is allowed.
export const getClashingLoopArrayVariables = (
  steps: Array<Step>,
  loopStep: Step,
  loopVariables: Array<LoopVariable> = []
): Array<string> => {
  const inUse = new Set(loopVariables.map(({ variableName }) => variableName));

  return [...new Set(getLoopArrayVariableNames(steps, loopStep))].filter(
    (name) => inUse.has(name)
  );
};

const ITEM_VARIABLE_SUFFIX = '_item';

// The array name is trimmed rather than the suffix, so a long name still reads
// as that array's item variable.
export const makeItemVariableName = (arrayVariableName: string) =>
  `${arrayVariableName.slice(
    0,
    MAX_VARIABLE_LENGTH - ITEM_VARIABLE_SUFFIX.length
  )}${ITEM_VARIABLE_SUFFIX}`;

// Every variable name in the automation - item reference variables have to be
// unique against all of them, not just against other item variables.
export const getAllVariableNames = (variables: Array<any> = []) =>
  variables
    .map((v) => v?.details?.variable?.name)
    .filter(Boolean) as Array<string>;

// Item reference variables fall back to `<name>_1`, `<name>_2`, ... when the
// generated name is taken, rather than the `_copy_N` convention getUniqueName
// uses for duplicated variables.
export const getUniqueItemVariableName = (
  base: string,
  taken: Array<string>
) => {
  const names = new Set(taken);
  const trimmed = base.trim();

  // An item variable's name keeps its _item suffix whatever has to be cut to
  // fit, so the array name in front of it is what gets shortened.
  const keep = trimmed.endsWith(ITEM_VARIABLE_SUFFIX)
    ? ITEM_VARIABLE_SUFFIX
    : '';
  const stem = trimmed.slice(0, trimmed.length - keep.length);

  // Trimmed to make room for the suffix and counter, so the name stays within
  // MAX_VARIABLE_LENGTH however many are taken.
  const fit = (counter: string) =>
    `${stem.slice(0, MAX_VARIABLE_LENGTH - keep.length - counter.length)}${keep}${counter}`;

  const value = fit('');

  if (!names.has(value)) {
    return value;
  }

  let i = 1;
  while (names.has(fit(`_${i}`))) {
    i += 1;
  }
  return fit(`_${i}`);
};

// The item reference variable name the Loop wizard shows for an array choice.
// It's worked out against the loop as saved - what the save compares to - not
// the previous choice in the wizard, or swapping away and back again would pick
// a fresh name the save never creates a variable for.
export const getWizardItemVariableName = (
  arrayVariable: any,
  savedForEachItem: any,
  taken: Array<string>
) => {
  const savedItemVariable = savedForEachItem?.itemVariable;

  if (
    savedForEachItem?.arrayVariable &&
    savedItemVariable &&
    getItemVariableChange(
      { arrayVariable, itemVariable: savedItemVariable },
      savedForEachItem
    ) !== ITEM_VARIABLE_CHANGES.Replace
  ) {
    return savedItemVariable;
  }

  return getUniqueItemVariableName(
    makeItemVariableName(arrayVariable.value),
    taken
  );
};

// The item reference variable is a non-array clone of the array variable it
// iterates, initialized alongside the automation's other variables.
export const createItemVariableStepData = (
  name: string,
  sourceArrayVariable: string,
  uniqueTime: string,
  parentStepId: string,
  order: string,
  orgDetails: any
) => {
  const id = `new.initialize_variable.${uniqueTime}`;
  const { id: _id, ...variable } = orgDetails.variable;

  return {
    id,
    stepType: 'initialize_variable',
    order,
    description: 'Item Reference Variable',
    userDescription: '',
    parentStepId,
    parentCondition: '',
    actionOnFailure: 'notify_pause',
    shouldSkipExecution: false,
    errorNotificationSeverityLevel: 'inherit',
    isBranchGroupInitiator: false,
    type: 'initialize_variable',
    details: {
      ...orgDetails,
      id,
      type: 'initialize_variable',
      variable: {
        ...variable,
        name,
        isArrayItemVariable: true,
        sourceArrayVariable,
        isArray: false,
      },
      sources: [],
      sourcesMask: [],
      arrayAggregationMode: 'add_all',
      deduplicateArray: false,
      isRequired: false,
    },
    hasError: false,
    considerEmptyAnError: false,
    stepError: null,
  };
};

// Appends an item reference variable, keeping the ordering insertVariable()
// establishes - item variables sit after the regular ones and the add button -
// and re-chaining parentStepId across the whole list.
export const insertItemVariable = (variables: Array<any>, step: any) => {
  const { regularVariables, addNew, itemVariables } = variables.reduce(
    (prev, v) => {
      if (v.type === 'initialize_variable_add') {
        prev.addNew.push(v);
      } else if (!v?.details?.variable?.isArrayItemVariable) {
        prev.regularVariables.push(v);
      } else {
        prev.itemVariables.push(v);
      }
      return prev;
    },
    { regularVariables: [], addNew: [], itemVariables: [] } as {
      regularVariables: Array<any>;
      addNew: Array<any>;
      itemVariables: Array<any>;
    }
  );

  let parentStepId: string | null = null;

  return [...regularVariables, ...addNew, ...itemVariables, step].map(
    (v, order) => {
      const ret = { ...v, order, parentStepId };
      parentStepId = v.id;
      return ret;
    }
  );
};

export const getImmediateChildren = (steps: Array<Step>, parent: Step) =>
  steps.filter((step) => step.parentKey === parent?.id);

export const getImmediateChildrenIds = (steps: Array<Step>, parent: Step) =>
  getImmediateChildren(steps, parent).map(({ id }) => id);

export const hasChildren = (steps: Array<Step>, parent: Step) =>
  steps.some((step) => step.parentKey === parent.id);

export const getJoinStep = (steps: Array<Step>, parent: Step) =>
  steps.find(
    (step) =>
      step.parentKey === parent?.id && step.type === BranchStepTypes.MERGE
  );

export const doesJoinStepHaveChildren = (steps: Array<Step>, parent: Step) => {
  const joinStep = getJoinStep(steps, parent);
  if (!joinStep) {
    return false;
  }
  return hasChildren(steps, joinStep);
};

export const doesJoinStepHaveChildrenById = (
  steps: Array<Step>,
  parentId: string
) => {
  const parent = steps.find((s) => s.id === parentId);

  if (!parent) {
    return false;
  }
  const joinStep = getJoinStep(steps, parent);

  return joinStep ? hasChildren(steps, joinStep) : false;
};

export const getSplitStep = (steps: Array<Step>, parent: Step) =>
  steps.find(
    (step) =>
      step.parentKey === parent?.id && step.type === BranchStepTypes.SPLIT
  );

export const doesSplitStepHaveChildren = (steps: Array<Step>, parent: Step) => {
  const splitStep = getSplitStep(steps, parent);
  if (!splitStep) {
    return false;
  }
  return hasChildren(steps, splitStep);
};

export const doesSplitStepHaveChildrenById = (
  steps: Array<Step>,
  parentId: string
) => {
  const parent = steps.find((s) => s.id === parentId);

  if (!parent) {
    return false;
  }

  const splitStep = getSplitStep(steps, parent);

  return splitStep ? hasChildren(steps, splitStep) : false;
};

// we are going to insert place holder / ghost steps to make it easier to insert the new merge and split steps

export const createMergeSplitStepData = (
  parentKey: string,
  isBranchMerged: boolean,
  goalType: boolean = false,
  mergeParent: string | null,
  loopDepth: number = 0,
  loopVariables: Array<LoopVariable> = []
) => ({
  id: `${parentKey}.merge-split`,
  type: BranchStepTypes.SPLIT,
  parentKey,
  fromLabel: '',
  prefix: 'step',
  goalType,
  details: {},
  hasError: false,
  description: 'Split',
  userDescription: '',
  stats: {
    numberCompleted: 0,
    numberFailed: 0,
    numberPending: 0,
    numberYes: 0,
    numberNo: 0,
  },
  parentCondition: '',
  actionOnFailure: 'notify_pause',
  shouldSkipExecution: false,
  errorNotificationSeverityLevel: 'inherit',
  isBranchGroupInitiator: false,
  isBranchMerged,
  mergeParent,
  loopDepth,
  loopVariables,
});

export const createMergeStepData = (
  parentKey: string,
  userDescription: string,
  loopDepth: number = 0,
  loopVariables: Array<LoopVariable> = []
) => ({
  id: `new.${BranchStepTypes.MERGE}.${Date.now()}`,
  type: BranchStepTypes.MERGE,
  parentKey,
  fromLabel: '',
  prefix: 'step',
  goalType: false,
  details: {},
  hasError: false,
  description: 'Merge Branch',
  userDescription,
  stats: {
    numberCompleted: 0,
    numberFailed: 0,
    numberPending: 0,
    numberYes: 0,
    numberNo: 0,
  },
  parentCondition: '',
  actionOnFailure: 'notify_pause',
  shouldSkipExecution: false,
  errorNotificationSeverityLevel: 'inherit',
  isBranchGroupInitiator: false,
  isBranchMerged: false,
  mergeParent: parentKey,
  loopDepth,
  loopVariables,
});

export const createBranchSplitStepData = (
  parentKey: string,
  isBranchMerged: boolean,
  goalType: boolean = false,
  mergeParent: string | null,
  loopDepth: number = 0,
  loopVariables: Array<LoopVariable> = []
) => ({
  id: `${parentKey}.merge-split`,
  type: BranchStepTypes.BRANCH_SPLIT,
  parentKey,
  fromLabel: '',
  prefix: 'step',
  goalType,
  details: {},
  hasError: false,
  description: 'Split',
  userDescription: '',
  stats: {
    numberCompleted: 0,
    numberFailed: 0,
    numberPending: 0,
    numberYes: 0,
    numberNo: 0,
  },
  parentCondition: '',
  actionOnFailure: 'notify_pause',
  shouldSkipExecution: false,
  errorNotificationSeverityLevel: 'inherit',
  isBranchGroupInitiator: false,
  isBranchMerged,
  mergeParent,
  loopDepth,
  loopVariables,
});

export const createLoopNextStepData = (
  parentKey: string,
  loopDepth: number,
  loopVariables: Array<LoopVariable>,
  userDescription: string,
  description: string
) => ({
  id: `new.${LoopStepTypes.LOOP_NEXT_OR_FINISH}.${Date.now()}`,
  type: LoopStepTypes.LOOP_NEXT_OR_FINISH,
  parentKey,
  loopParent: parentKey,
  fromLabel: '',
  prefix: 'step',
  goalType: false,
  details: {},
  hasError: false,
  description,
  userDescription,
  stats: {
    numberCompleted: 0,
    numberFailed: 0,
    numberPending: 0,
    numberYes: 0,
    numberNo: 0,
  },
  parentCondition: '',
  actionOnFailure: 'notify_pause',
  shouldSkipExecution: false,
  errorNotificationSeverityLevel: 'inherit',
  isBranchGroupInitiator: false,
  isBranchMerged: false,
  mergeParent: null,
  loopDepth,
  loopVariables,
});

export const createBranchStepData = (
  id: string,
  parentKey: string,
  fromLabel: string,
  isBranchGroupInitiator: boolean = false,
  description: string,
  loopDepth: number = 0,
  loopVariables: Array<LoopVariable> = []
) => ({
  description,
  goalType: undefined,
  isBranchGroupInitiator,
  prefix: 'step',
  type: BranchStepTypes.BRANCH,
  userDescription: '',
  id,
  parentKey,
  fromLabel,
  details: {},
  loopDepth,
  loopVariables,
});

export const isConditionOrGoalSplit = (step: Partial<Step>) =>
  step?.type === BranchStepTypes.SPLIT;

export const isMergeJoin = (step: Partial<Step>) =>
  step?.type === BranchStepTypes.MERGE;

export const isBranchStep = (step: Partial<Step>) =>
  step?.type === BranchStepTypes.BRANCH;

export const isBranchSplit = (step: Partial<Step>) =>
  step?.type === BranchStepTypes.BRANCH_SPLIT;

export const isGoalOrCondition = (step: Partial<Step>) =>
  step.type === 'condition' || step.goalType;

export const isLoopStep = (step: Partial<Step>) =>
  step?.type === LoopStepTypes.LOOP;

export const isLoopStepNext = (step: Partial<Step>) =>
  step?.type === LoopStepTypes.LOOP_NEXT_OR_FINISH;

export const getLoopNextStep = (steps: Array<Step>, loopStep: Step) =>
  steps.find(
    (step) => isLoopStepNext(step) && step.loopParent === loopStep?.id
  );

// Steps nested as deeply as loops are allowed to go — no further loop may be
// added inside these. A loop at the limit closes on a Next/Finish step that
// sits a level shallower, so it is pulled in by hand rather than by depth.
export const getStepIdsAtMaxLoopDepth = (steps: Array<Step>): Array<string> => {
  const ids = steps.reduce((acc, step) => {
    if ((step.loopDepth ?? 0) < MAX_LOOP_DEPTH) {
      return acc;
    }

    acc.add(step.id);

    if (isLoopStep(step)) {
      const loopNextStep = getLoopNextStep(steps, step);

      if (loopNextStep) {
        acc.add(loopNextStep.id);
      }
    }

    return acc;
  }, new Set<string>());

  return [...ids];
};

// The ids of every step sitting between a loop and the Next/Finish step that
// closes it. Branches and nested loops are walked in full; neither the loop
// itself nor its Next/Finish step is included.
export const getStepIdsInLoop = (
  steps: Array<Step>,
  loopStep: Step
): Array<string> => {
  const loopNextStep = getLoopNextStep(steps, loopStep);

  if (!loopStep || !loopNextStep) {
    return [];
  }

  const ids: Array<string> = [];
  // seeding with both ends stops the walk at the Next/Finish step and guards
  // against a malformed tree looping back on itself
  const seen = new Set<string>([loopStep.id, loopNextStep.id]);

  const queue = steps.filter(
    (step) => step.parentKey === loopStep.id && !seen.has(step.id)
  );

  while (queue.length) {
    const current = queue.shift();

    if (!current || seen.has(current.id)) {
      continue;
    }

    seen.add(current.id);
    ids.push(current.id);

    queue.push(
      ...steps.filter(
        (step) => step.parentKey === current.id && !seen.has(step.id)
      )
    );
  }

  return ids;
};

export const getMergeAndSplitSteps = (tree: Array<Step>, parent: Step) => {
  let splitStep = undefined;
  let mergeStep = undefined;

  if (isGoalOrCondition(parent)) {
    splitStep = tree.find(
      (s) => s.parentKey === parent.id && isConditionOrGoalSplit(s)
    );
    mergeStep = tree.find((s) => s.parentKey === parent.id && isMergeJoin(s));
  } else if (isBranchStep(parent)) {
    splitStep = tree.find((s) => s.parentKey === parent.id && isBranchSplit(s));
    mergeStep = tree.find((s) => s.parentKey === parent.id && isMergeJoin(s));
  }
  return { splitStep, mergeStep };
};

export const findTheLastLeavesFromBranch = (
  tree: Array<Step>,
  parentKey: string,
  fromLabel: string
) => {
  if (!tree || !parentKey || !fromLabel) {
    return [];
  }

  const roots = tree.filter((step) => {
    return step.parentKey === parentKey && step.fromLabel === fromLabel;
  });

  if (!roots.length) {
    return [];
  }

  const leaves = [];
  const queue = roots;

  while (queue.length > 0) {
    const current = queue.shift();

    if (!current) {
      continue;
    }

    let children = tree.filter(
      (step) =>
        step.parentKey === current?.id && step.type !== BranchStepTypes.MERGE
    );

    if (!children || children.length === 0) {
      leaves.push(current);
    } else {
      if (
        isGoalOrCondition(current) &&
        current.isBranchGroupInitiator &&
        current.id !== parentKey
      ) {
        // going to skip this whole merged branch
        const sourceStep = tree.find(
          (step) =>
            step.parentKey === current.id && step.type === BranchStepTypes.MERGE
        );

        children = tree.filter(
          (step) =>
            step.parentKey === sourceStep?.id &&
            step.type !== BranchStepTypes.MERGE
        );

        if (!children.length) {
          // no children of merge then the merge is the end
          leaves.push({ sourceStep, branches: [''] });
        }
      }

      queue.push(...children);
    }
  }

  return leaves;
};

export const countTheLastLeavesFromBranch = (
  tree: Array<Step>,
  parentKey: string,
  fromLabel: string
) => {
  if (!tree || !parentKey || !fromLabel) {
    return 2;
  }

  const roots = tree.filter((step) => {
    return step.parentKey === parentKey && step.fromLabel === fromLabel;
  });

  if (!roots.length) {
    return 0;
  }

  let leaves = 0;
  const queue = roots;

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) {
      continue;
    }
    let children = tree.filter(
      (step) =>
        step.parentKey === current.id && step.type !== BranchStepTypes.MERGE
    );

    if (!children || children.length === 0) {
      leaves++;
    } else {
      if (
        isGoalOrCondition(current) &&
        current.isBranchGroupInitiator &&
        current.id !== parentKey
      ) {
        // going to skip this whole merged branch
        const sourceStep = tree.find(
          (step) =>
            step.parentKey === current.id && step.type === BranchStepTypes.MERGE
        );

        children = tree.filter(
          (step) =>
            step.parentKey === sourceStep?.id &&
            step.type !== BranchStepTypes.MERGE
        );

        if (!children.length) {
          // no children of merge then the merge is the end
          leaves += 2;
        }
      }

      queue.push(...children);
    }
  }

  return leaves;
};

export const getChildrenSteps = (
  parentId: string,
  parentIds: Array<string>,
  steps: Array<Step>
): Array<string> => {
  const mergeStep: Array<string> = [];

  const children = steps.reduce((acc, item) => {
    if (parentIds.includes(item.parentKey || '')) {
      if (item.type === BranchStepTypes.MERGE && item.parentKey && parentId) {
        mergeStep.push(item.id);
      } else {
        return [...acc, item.id];
      }
    }
    return acc;
  }, [] as Array<string>);

  return children.length
    ? [
        ...children,
        ...mergeStep,
        ...getChildrenSteps(parentId, children, steps),
      ]
    : [...children, ...mergeStep];
};

export const getMergeDescription = (type: string, t: TFunction) =>
  type === BranchStepTypes.BRANCH
    ? t('Run Branches in Parallel, then Merge')
    : undefined;

export const getUnmergeDescription = (type: string, t: TFunction) =>
  type === BranchStepTypes.BRANCH ? t('Run Branches in Parallel') : undefined;

export const getStepsInMerge = (
  tree: Array<Step>,
  parentKey: string,
  mergeStepId: string
) => {
  if (!tree) {
    return [];
  }

  const root = tree.find((step) => step.id === parentKey);

  if (!root) {
    return [];
  }

  const leaves = [];
  const queue = [root];

  while (queue.length > 0) {
    const current = queue.shift();

    if (current && current?.id !== mergeStepId) {
      if (current.id !== parentKey) {
        leaves.push(current?.id);
      }
      const children = tree.filter((step) => step.parentKey === current.id);
      queue.push(...children);
    }
  }

  return leaves;
};

export const getStepsInMergeMovingBranch = (
  id: string,
  steps: Array<Step>,
  includeMerge: boolean = true
): Array<string> => {
  const root = steps.find((s) => s.id === id);
  if (!root) {
    return [];
  }

  const { splitStep, mergeStep } = getMergeAndSplitSteps(steps, root);

  if (!splitStep || !mergeStep) {
    return [];
  }

  const stepsInMerge = getStepsInMerge(steps, id, mergeStep.id);

  if (includeMerge) {
    stepsInMerge.push(mergeStep.id);
  }
  return stepsInMerge;
};

export const getAllStepIdsInMergedBranches = (steps: Array<Step>) => {
  const stepIdsInMergedBranches = steps
    .filter(
      (step) =>
        step.isBranchGroupInitiator &&
        (isBranchStep(step) || isGoalOrCondition(step))
    )
    .reduce(
      (prevBgi, bgi) => {
        const stepsIdsInMergeBranch = getStepsInMergeMovingBranch(
          bgi.id,
          steps,
          false
        );
        stepsIdsInMergeBranch.forEach((stepId) => {
          if (!prevBgi[stepId]) {
            prevBgi = { ...prevBgi, [stepId]: true };
          }
        });
        return prevBgi;
      },
      {} as Record<string, boolean>
    );

  return stepIdsInMergedBranches;
};
// checking that action/trigger don't have relations to `Go To Step Action`

export const ensureNoGoToStepDependency = (
  automationSteps: Array<Step>,
  id: string
) => {
  return automationSteps.some((step) => {
    const data =
      step.details && (step.details.stepKey || step.details.triggerKey);
    return (
      step.type === AUTOMATION_STEPS.go_to_automation_step.type &&
      data?.value === id
    );
  });
}; // get a list of that action/trigger have relations to `Go To Step Action`

export const getAllGoToStepDependencies = (
  automationSteps: Array<Step>,
  id: string
) => {
  return automationSteps.filter((step) => {
    const data =
      step.details && (step.details.stepKey || step.details.triggerKey);
    return (
      step.type === AUTOMATION_STEPS.go_to_automation_step.type &&
      data?.value === id
    );
  });
};

export const getDescendantStepIds = (
  automationSteps: Array<Step>,
  id: string
): Array<string> => {
  return automationSteps.reduce((acc: Array<string>, step) => {
    if (step.parentKey === id) {
      return [
        ...acc,
        step.id,
        ...getDescendantStepIds(automationSteps, step.id),
      ];
    }
    return acc;
  }, []);
};

export const getAllGoToStepDependenciesForSubtree = (
  automationSteps: Array<Step>,
  deletedIds: Array<string>
) => {
  const deletedIdsSet = new Set(deletedIds);
  const dependencies = deletedIds.flatMap((stepId) =>
    getAllGoToStepDependencies(automationSteps, stepId)
  );
  // exclude GoToSteps that are themselves being deleted
  return dependencies.filter((step) => !deletedIdsSet.has(step.id));
};

export const clearGoToStepReferenceIfDeleted = (
  step: Step,
  deletedIds: Array<string>,
  t: TFunction
): Step => {
  if (!step.details) {
    return step;
  }

  const deletedIdsSet = new Set(deletedIds);
  const { stepKey, triggerKey, ...restDetails } = step.details;

  if (deletedIdsSet.has(stepKey?.value || triggerKey?.value)) {
    const description = `${t('Go To Step')} [${t('Deleted')}]`;
    return {
      ...step,
      details: { ...restDetails },
      description,
    };
  }

  return step;
};

export const getChildrenOfUnmergedBranch = (
  tree: Array<Step>,
  parentKey: string,
  branches: Record<string, any>
) => {
  if (!tree) {
    return [];
  }

  const root = tree.find((step) => step.id === parentKey);
  if (!root) {
    return [];
  }

  const ids = [];
  const queue = [root];

  while (queue.length > 0) {
    const current = queue.shift();
    let children = tree.filter(
      (step) =>
        step.parentKey === current?.id && step.type !== BranchStepTypes.MERGE
    );

    if (parentKey !== current?.id) {
      ids.push(current?.id);
    }
    if (children && children.length) {
      if (
        (isGoalOrCondition(current!) || isBranchStep(current!)) &&
        current?.isBranchGroupInitiator &&
        current?.id !== parentKey
      ) {
        // going to skip this whole merged branch
        const mergeStep = tree.find(
          (step) =>
            step.parentKey === current.id && step.type === BranchStepTypes.MERGE
        );

        const stepsInMergedBranch = getStepsInMergeMovingBranch(
          current.id,
          tree,
          true
        );

        ids.push(...stepsInMergedBranch);

        children = tree.filter(
          (step) =>
            step.parentKey === mergeStep?.id &&
            step.type !== BranchStepTypes.MERGE
        );
      } else {
        children = tree.filter((step) => step.parentKey === current?.id);
      }

      queue.push(...children);
    }
  }

  return ids;
};
